/**
 * useEntryRecoveryDrawer — reopens property selection over the catalogue when
 * the flow has no property.
 *
 * The store carries no `persist` middleware and a draft is restored only by an
 * explicit "Continue order", so a page reload on /purchase-flow-revamp/catalogue
 * drops `draft.property`. Country, locale and the whole priced product set
 * derive from it, so the page would otherwise sit empty forever.
 *
 * The drawer opens over the catalogue rather than redirecting, so the URL the
 * user reloaded stays put and they land back in place once they choose.
 *
 * The close handler carries the one piece of real logic here:
 * `PropertySelectionDrawer.proceedWithProperty` calls `setProperty` and THEN
 * `onClose`, so closing is not on its own evidence that the user dismissed the
 * drawer. Reading the store tells the two apart — a property present means they
 * picked one, and only a genuine dismiss should leave the flow.
 *
 * Inside the Propstack embed there is no drawer: the flow goes back to its entry, which restores the
 * CRM's property and cannot be closed, as leaving the flow would load the main app into the CRM.
 */

import { useCallback, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'

import { Path } from 'constants/router'
import { usePfStore } from '../../store'
import { PfStepPath, usePfPath } from '../../routing/pfBasePath'
import { usePfEmbed } from '../../routing/usePfEmbed'

interface EntryRecoveryDrawer {
  /** True while the flow has no property — drives the drawer's `isOpen`. */
  isOpen: boolean
  /** Leaves the flow only when the drawer was genuinely dismissed. */
  onClose: () => void
  /** Overrides the drawer's default navigation so the user stays put. */
  onProceed: () => void
}

export function useEntryRecoveryDrawer(): EntryRecoveryDrawer {
  const navigate = useNavigate()
  const pfPath = usePfPath()
  const { isEmbed } = usePfEmbed()
  const hasNoProperty = usePfStore((s) => !s.property)

  useEffect(() => {
    if (isEmbed && hasNoProperty) navigate(pfPath(PfStepPath.SELECT_PROPERTY), { replace: true })
  }, [isEmbed, hasNoProperty, navigate, pfPath])

  const onClose = useCallback(() => {
    if (isEmbed || usePfStore.getState().property) return
    navigate(Path.INDEX)
  }, [isEmbed, navigate])

  const onProceed = useCallback(() => undefined, [])

  return { isOpen: hasNoProperty && !isEmbed, onClose, onProceed }
}
